import { CONFIG } from '@/constants/Config';
import {
  mapCommentTemplateTypeFromApi,
  normalizeCommentTemplatePoste,
  parseCommentTemplatePro,
  type CommentTemplate,
} from '@/constants/observationCommentTemplates';
import { matchReactionCle } from '@/utils/matchReactionCle';
import { emptyMatchReactionCounts } from '@/constants/matchReactions';
import { enrichObservationWithMatch } from '@/utils/enrichObservationWithMatch';
import { favoriteClubCle, favoriteMatchCle } from '@/utils/favoriteCle';
import { parseFavoritesJson, serializeFavoritesJson, type FavoritesData } from '@/utils/favoritesJson';
import { resolveObservationPrivate } from '@/utils/observationPrivacy';
import {
  matchesObservationOwner,
  parseEquipeRaw,
  resolveTeamFromClubRef,
  resolveTeamFromTeamNames,
} from './observationTeam';
import { storageService } from './storage';
import type { Match, Club, Observation, Tifosi, ApiResponse, FanZoneComment, MatchReactionType, MatchReactionsSummary } from '@/types';

type RawClub = Record<string, unknown>;
type RawMatch = Record<string, unknown>;
type RawObservation = Record<string, unknown>;
type RawTifosi = Record<string, unknown>;
type RawFanZoneComment = Record<string, unknown>;
type RawMatchReaction = Record<string, unknown>;
type RawCommentTemplate = Record<string, unknown>;

const VALID_MATCH_REACTIONS = new Set<MatchReactionType>(['going', 'force', 'like', 'dislike', 'fire']);

export interface TifosiPayload {
  cle: string;
  nom?: string | null;
  prenom?: string | null;
  email?: string | null;
  mobile?: string | null;
  pseudo?: string | null;
  club?: string | null;
  /** JSON array d’identifiants club (champ Memo GoPaaS `favoris_clubs`). */
  favoris_clubs?: string | null;
  /** JSON array d’identifiants match (champ Memo GoPaaS `favoris_matchs`). */
  favoris_matchs?: string | null;
}
export interface ObservationPayload {
  cle: string;
  nom: string;
  prenom: string;
  poste: string;
  numero: string;
  technique: string;
  tactique: string;
  mental: string;
  vitesse: string;
  physique: string;
  but: string;
  passe_decisive: string;
  observations_generales: string | null;
  match_football: string;
  equipe?: 'home' | 'away';
  club?: string;
  tifosi?: string;
  home_team?: string;
  away_team?: string;
  home_club_id?: string;
  away_club_id?: string;
  /** 1 = visible uniquement par l’auteur, 0 = publique (Checkbox GoPaaS). */
  prive?: '0' | '1' | 0 | 1;
  qualite_contenu_score: number;
  qualite_contenu_niveau: 'FAIBLE' | 'MOYENNE' | 'ELEVEE';
  qualite_commentaire_mots: number;
  qualite_commentaire_concret: 0 | 1;
  qualite_notes_coherence: 0 | 1;
  geo_verification_statut: 'NON_VERIFIE' | 'PROCHE_MATCH' | 'HORS_ZONE' | 'INCERTAIN' | 'INDISPONIBLE';
  geo_distance_m: number | null;
  geo_accuracy_m: number | null;
  geo_seuil_m: number;
  geo_verifie_at: string | null;
  score_confiance?: number;
  niveau_confiance?: 'FAIBLE' | 'MOYENNE' | 'ELEVEE';
  note_robuste?: number;
  observation_provisoire?: 0 | 1;
}
export interface TifosiFavoriteClubPayload {
  cle: string;
  tifosi: string;
  club: string;
  actif?: '0' | '1' | 0 | 1;
}

export interface TifosiFavoriteMatchPayload {
  cle: string;
  tifosi: string;
  match_football: string;
  actif?: '0' | '1' | 0 | 1;
}

export interface FanZoneCommentPayload {
  cle: string;
  message: string;
  match_football: string;
  tifosi: string;
}

export interface MatchReactionPayload {
  cle: string;
  tifosi: string;
  match_football: string;
  reaction: MatchReactionType;
  actif: '0' | '1';
}

/** Événements d’usage enregistrés dans GoPaaS (`api/items/tifosi_event`). */
export type UsageEventType =
  | 'match_opened'
  | 'observation_opened'
  | 'club_opened'
  | 'observation_created'
  | 'login_completed'
  | 'map_opened'
  | 'fanzone_opened'
  | 'fanzone_comment'
  | 'club_favorited'
  | 'club_unfavorited'
  | 'match_favorited'
  | 'match_unfavorited';

/** Corps pour l’API — ne pas y ajouter `date_creation` / `heure_creation` (remplis par GoPaaS). */
export interface UsageEventPayload {
  cle: string;
  type: UsageEventType;
  tifosi: string;
  match_football?: string;
  club?: string;
  observation?: string;
  /** Canal d’acquisition (UTM, referrer, in-app browser…). */
  source?: string;
}

/** Données sans `cle` — `createUsageEvent` génère une clé unique par fiche. */
export type UsageEventInput = Omit<UsageEventPayload, 'cle'>;

function flattenRaw(raw: RawMatch): RawMatch {
  const basic = raw.basic ?? raw.data ?? raw.item;
  if (basic && typeof basic === 'object' && !Array.isArray(basic)) {
    return { ...raw, ...basic } as RawMatch;
  }
  return raw;
}

function pickMatchEpreuvesKey(flat: RawMatch): string | undefined {
  const keys = ['match_key', 'matchKey', 'cle_match', 'id_epreuves', 'id_match_epreuves', 'key_match', 'cle'];
  for (const key of keys) {
    const val = flat[key];
    if (val == null || val === '') continue;
    if (typeof val === 'number' && Number.isFinite(val)) return String(val);
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  return undefined;
}

function extractDate(raw: RawMatch): string | undefined {
  const flat = flattenRaw(raw);
  const dateFields = ['date', 'date_match', 'dateMatch', 'date_match_football', 'date_debut', 'match_date', 'start_date'];
  for (const key of dateFields) {
    const val = flat[key];
    if (typeof val === 'string' && val.trim()) return val.trim();
    if (val instanceof Date && !isNaN(val.getTime())) return val.toISOString().slice(0, 10);
    if (typeof val === 'number' && !isNaN(val)) return new Date(val).toISOString().slice(0, 10);
  }
  for (const key of Object.keys(flat)) {
    if (key.toLowerCase().includes('date') && typeof flat[key] === 'string') {
      const s = String(flat[key]).trim();
      if (s && /^\d{4}-\d{2}-\d{2}/.test(s)) return s;
    }
  }
  return undefined;
}

function normalizeMatch(raw: RawMatch): Match {
  const flat = flattenRaw(raw);
  const date = extractDate(raw) ?? '';
  return {
    idmatch_football: String(flat.idmatch_football ?? flat.id ?? flat.cle ?? ''),
    cle: flat.cle ? String(flat.cle) : undefined,
    match_key: pickMatchEpreuvesKey(flat),
    homeTeam: String(flat.homeTeam ?? flat.home ?? ''),
    awayTeam: String(flat.awayTeam ?? flat.away ?? ''),
    home_logo: flat.home_logo ? String(flat.home_logo) : undefined,
    away_logo: flat.away_logo ? String(flat.away_logo) : undefined,
    home_score: flat.home_score != null ? Number(flat.home_score) : null,
    away_score: flat.away_score != null ? Number(flat.away_score) : null,
    date,
    heure: String(flat.heure ?? flat.heure_match ?? ''),
    competition: String(flat.competition ?? flat.competion ?? ''),
    ville: flat.ville ? String(flat.ville) : undefined,
    nom: flat.nom ? String(flat.nom) : undefined,
    stade: flat.stade ? String(flat.stade) : undefined,
    terrain: flat.terrain ? String(flat.terrain) : undefined,
    cp: flat.cp ? String(flat.cp) : undefined,
    latitude: flat.latitude ? String(flat.latitude) : undefined,
    longitude: flat.longitude ? String(flat.longitude) : undefined,
    journee: flat.journee ? String(flat.journee) : undefined,
    surface: flat.surface ? String(flat.surface) : undefined,
    poule: pickString(flat as RawObservation, ['poule', 'match_poule', 'poule_match', 'libelle_poule']),
  };
}

function pickNumber(raw: RawObservation, keys: string[]): number | undefined {
  for (const key of keys) {
    const val = raw[key];
    if (val == null || val === '') continue;
    const n = typeof val === 'number' ? val : Number(val);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** Checkbox GoPaaS : true uniquement si explicitement coché (1 / true / oui / x). */
function pickCheckboxTrue(raw: RawObservation, keys: string[]): boolean | undefined {
  for (const key of keys) {
    const val = raw[key];
    if (val == null || val === '') continue;
    if (val === true || val === 1 || val === '1') return true;
    if (val === false || val === 0 || val === '0') return false;
    if (typeof val === 'string') {
      const normalized = val.trim().toLowerCase();
      if (normalized === 'true' || normalized === 'oui' || normalized === 'x' || normalized === 'on') return true;
      if (normalized === 'false' || normalized === 'non' || normalized === 'off') return false;
    }
  }
  return undefined;
}

function pickString(raw: RawObservation, keys: string[]): string | undefined {
  for (const key of keys) {
    const val = raw[key];
    if (typeof val === 'string' && val.trim()) return val.trim();
    if (typeof val === 'number') return String(val);
  }
  return undefined;
}

function stringFromUnknown(val: unknown): string | undefined {
  if (typeof val === 'string' && val.trim()) return val.trim();
  if (typeof val === 'number' && Number.isFinite(val)) return String(val);
  return undefined;
}

function cleFromRelation(val: unknown): string | undefined {
  if (!val || typeof val !== 'object' || Array.isArray(val)) return undefined;
  const obj = val as Record<string, unknown>;
  return (
    stringFromUnknown(obj.cle) ??
    stringFromUnknown(obj.id) ??
    stringFromUnknown(obj.idtifosi) ??
    stringFromUnknown(obj.tifosi_cle) ??
    stringFromUnknown(obj.idclub) ??
    stringFromUnknown(obj.idmatch_football)
  );
}

function pickConnexionId(raw: RawObservation, keys: string[]): string | undefined {
  for (const key of keys) {
    const val = raw[key];
    const direct = stringFromUnknown(val) ?? cleFromRelation(val);
    if (direct) return direct;
  }
  return undefined;
}

function isFavoriteRowActive(raw: RawObservation): boolean {
  const actif = raw.actif;
  if (actif == null || actif === '') return true;
  if (actif === false || actif === 0 || actif === '0') return false;
  return true;
}

function looksLikePersonName(value: string): boolean {
  const v = value.trim();
  if (!v || parseEquipeRaw(v)) return false;
  if (/^[0-9a-f-]{8,}$/i.test(v)) return false;
  return /\s/.test(v) || /^[A-Za-zÀ-ÿ]{2,}$/.test(v);
}

/** Extrait la clé Tifosi (y compris relation GoPaaS ou champ `team` si ce n'est pas un côté domicile/extérieur). */
function pickTifosiCle(flat: RawObservation): string | undefined {
  for (const key of ['tifosi', 'idtifosi', 'tifosi_cle', 'createur_cle', 'id_createur', 'tifosi_createur']) {
    const val = flat[key];
    const direct = stringFromUnknown(val) ?? cleFromRelation(val);
    if (direct) return direct;
  }
  for (const key of ['createur', 'observateur']) {
    const val = flat[key];
    const direct = stringFromUnknown(val) ?? cleFromRelation(val);
    if (direct && !looksLikePersonName(direct)) return direct;
  }
  const teamVal = flat.team;
  const teamStr = stringFromUnknown(teamVal) ?? cleFromRelation(teamVal);
  if (teamStr && !parseEquipeRaw(teamStr) && !looksLikePersonName(teamStr)) return teamStr;
  return undefined;
}

/** Valeur brute du côté équipe (`equipe`), sans confondre avec la clé Tifosi portée par `team`. */
function pickEquipeRawValue(flat: RawObservation): string | undefined {
  const equipe = pickString(flat, ['equipe']);
  if (equipe) return equipe;
  for (const key of ['team_side', 'side'] as const) {
    const side = pickString(flat, [key]);
    if (side && parseEquipeRaw(side)) return side;
  }
  const team = pickString(flat, ['team']);
  if (team && parseEquipeRaw(team)) return team;
  return undefined;
}

/** Compétition / poule depuis la relation match imbriquée (GoPaaS). */
function pickMatchMetaFromNested(flat: RawObservation): {
  competition?: string;
  poule?: string;
} {
  for (const key of ['match_football', 'match', 'matchFootball', 'match_foot']) {
    const val = flat[key];
    if (!val || typeof val !== 'object' || Array.isArray(val)) continue;
    const nested = flattenRaw(val as RawMatch) as RawObservation;
    const competition = pickString(nested, [
      'competition',
      'competion',
      'competition_match',
      'libelle_competition',
      'categorie',
      'epreuve',
      'libelle_epreuve',
    ]);
    const poule = pickString(nested, [
      'poule',
      'match_poule',
      'poule_match',
      'libelle_poule',
      'poule_competition',
    ]);
    if (competition || poule) return { competition, poule };
  }
  return {};
}

function normalizeObservation(raw: RawObservation): Observation {
  const flat = flattenRaw(raw as RawMatch) as RawObservation;
  const nestedMatchMeta = pickMatchMetaFromNested(flat);
  const nom = pickString(flat, ['nom', 'last_name', 'lastName']) ?? '';
  const prenom = pickString(flat, ['prenom', 'first_name', 'firstName']) ?? '';
  const numero = pickString(flat, ['numero', 'number', 'jersey']);
  const poste = pickString(flat, ['poste', 'position']);
  const technique = pickNumber(flat, ['technique']);
  const tactique = pickNumber(flat, ['tactique']);
  const mental = pickNumber(flat, ['mental']);
  const vitesse = pickNumber(flat, ['vitesse', 'speed']);
  const physique = pickNumber(flat, ['physique']);
  const nbButs = pickNumber(flat, ['but', 'nb_buts', 'nbButs', 'nombre_but']);
  const nbPassesDecisives = pickNumber(flat, ['passe_decisive', 'passe_decisives', 'nb_passes_decisives', 'nbPassesDecisives']);
  const ratings = [technique, tactique, mental, vitesse, physique].filter((v): v is number => Number.isFinite(v as number));
  const rating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
  const matchId = pickString(flat, ['match', 'match_football', 'matchId', 'idmatch_football']);
  const matchCompetition =
    pickString(flat, [
      'competition_match',
      'competition',
      'competion',
      'libelle_competition',
      'categorie',
      'category',
      'epreuve',
      'libelle_epreuve',
    ]) ?? nestedMatchMeta.competition;
  const matchPoule =
    pickString(flat, [
      'poule',
      'match_poule',
      'poule_match',
      'libelle_poule',
      'poule_competition',
    ]) ?? nestedMatchMeta.poule;
  const homeTeam = pickString(flat, ['home_team', 'homeTeam']);
  const awayTeam = pickString(flat, ['away_team', 'awayTeam']);
  const homeClubId = pickString(flat, ['home_club_id', 'homeClubId']);
  const awayClubId = pickString(flat, ['away_club_id', 'awayClubId']);
  const clubRef = pickString(flat, ['club', 'cle_club']);
  const clubDisplayName = pickString(flat, ['club_name', 'clubName', 'equipe_nom', 'team_name', 'teamName']);
  const equipeField = pickString(flat, ['equipe']);
  const equipeSideRaw = pickEquipeRawValue(flat);
  const clubSideContext = { homeClubCle: homeClubId, awayClubCle: awayClubId, homeClubId, awayClubId };
  let team: 'home' | 'away' | undefined =
    parseEquipeRaw(equipeSideRaw) ?? parseEquipeRaw(equipeField);
  if (!team && equipeField) {
    team = resolveTeamFromClubRef(equipeField, clubSideContext);
  }
  if (!team && clubRef) {
    team = resolveTeamFromClubRef(clubRef, clubSideContext);
  }
  if (!team) {
    team = resolveTeamFromTeamNames(clubDisplayName ?? clubRef, homeTeam, awayTeam);
  }
  const club = clubRef ?? clubDisplayName;
  const teamName =
    clubDisplayName ??
    (team === 'home' ? homeTeam : team === 'away' ? awayTeam : undefined);
  const date =
    pickString(flat, ['date_creation', 'created_at', 'createdAt']) ??
    pickString(flat, ['date']) ??
    '';
  const tifosi = pickTifosiCle(flat);
  const author =
    pickString(flat, [
      'auteur',
      'author',
      'pseudo',
      'username',
      'createur',
      'nom_createur',
      'observateur',
      'tifosi_pseudo',
      'tifosi_nom',
    ]) ?? undefined;
  const id = pickString(flat, ['idobservation', 'id', 'cle']) ?? `obs-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const matchInfoParts = [homeTeam, awayTeam].filter(Boolean) as string[];
  const home_logo = pickString(flat, ['home_logo', 'homeLogo']);
  const away_logo = pickString(flat, ['away_logo', 'awayLogo']);
  const qualityScore = pickNumber(flat, ['qualite_contenu_score']);
  const qualityLevelRaw = pickString(flat, ['qualite_contenu_niveau']);
  const qualityLevel =
    qualityLevelRaw === 'FAIBLE' || qualityLevelRaw === 'MOYENNE' || qualityLevelRaw === 'ELEVEE'
      ? qualityLevelRaw
      : undefined;
  const qualityWordCount = pickNumber(flat, ['qualite_commentaire_mots']);
  const qualityCommentConcreteRaw = pickNumber(flat, ['qualite_commentaire_concret']);
  const qualityNotesCoherentRaw = pickNumber(flat, ['qualite_notes_coherence']);
  const qualityCommentConcrete =
    qualityCommentConcreteRaw === 0 || qualityCommentConcreteRaw === 1
      ? (qualityCommentConcreteRaw as 0 | 1)
      : undefined;
  const qualityNotesCoherent =
    qualityNotesCoherentRaw === 0 || qualityNotesCoherentRaw === 1
      ? (qualityNotesCoherentRaw as 0 | 1)
      : undefined;
  const geoStatusRaw = pickString(flat, ['geo_verification_statut']);
  const geoStatus =
    geoStatusRaw === 'NON_VERIFIE' ||
    geoStatusRaw === 'PROCHE_MATCH' ||
    geoStatusRaw === 'HORS_ZONE' ||
    geoStatusRaw === 'INCERTAIN' ||
    geoStatusRaw === 'INDISPONIBLE'
      ? geoStatusRaw
      : undefined;
  const geoDistanceM = pickNumber(flat, ['geo_distance_m']);
  const geoAccuracyM = pickNumber(flat, ['geo_accuracy_m']);
  const geoThresholdM = pickNumber(flat, ['geo_seuil_m']);
  const geoVerifiedAt = pickString(flat, ['geo_verifie_at']) ?? null;
  const confidenceScore = pickNumber(flat, ['score_confiance', 'confidence_score']);
  const confidenceLevelRaw = pickString(flat, ['niveau_confiance', 'confidence_level']);
  const confidenceLevel =
    confidenceLevelRaw === 'FAIBLE' || confidenceLevelRaw === 'MOYENNE' || confidenceLevelRaw === 'ELEVEE'
      ? confidenceLevelRaw
      : undefined;
  const robustRating = pickNumber(flat, ['note_robuste', 'robust_rating']);
  const provisionalRaw = pickNumber(flat, ['observation_provisoire', 'is_provisional']);
  const isProvisional = provisionalRaw === 1 ? true : provisionalRaw === 0 ? false : undefined;
  const observationCle = pickString(flat, ['cle']);
  const apiIsPrivate = pickCheckboxTrue(flat, ['prive', 'is_private', 'private', 'mode_prive']);
  const isPrivate = resolveObservationPrivate(apiIsPrivate, observationCle);
  return {
    id,
    cle: observationCle,
    nom,
    prenom,
    club,
    playerName: [prenom, nom].filter(Boolean).join(' ').trim() || nom || prenom || 'Joueur',
    playerNumber: numero ? `#${numero}` : undefined,
    poste,
    team,
    teamName,
    matchId,
    matchCompetition,
    matchPoule,
    matchInfo: matchInfoParts.length ? matchInfoParts.join(' vs ') : undefined,
    homeTeam,
    awayTeam,
    homeClubId,
    awayClubId,
    home_logo,
    away_logo,
    technique,
    tactique,
    mental,
    vitesse,
    physique,
    nbButs,
    nbPassesDecisives,
    rating,
    isPrivate: isPrivate ? true : undefined,
    comments: pickString(flat, ['observations_generales', 'comments', 'commentaire']),
    date,
    author,
    qualityScore,
    qualityLevel,
    qualityWordCount,
    qualityCommentConcrete,
    qualityNotesCoherent,
    geoStatus,
    geoDistanceM: geoDistanceM ?? null,
    geoAccuracyM: geoAccuracyM ?? null,
    geoThresholdM,
    geoVerifiedAt,
    confidenceScore,
    confidenceLevel,
    robustRating,
    isProvisional,
  };
}

function normalizeTifosi(raw: RawTifosi): Tifosi {
  const flat = flattenRaw(raw as RawMatch) as RawTifosi;
  return {
    cle: pickString(flat, ['cle', 'idtifosi', 'id']) ?? '',
    nom: pickString(flat, ['nom', 'last_name', 'lastName']),
    prenom: pickString(flat, ['prenom', 'first_name', 'firstName']),
    email: pickString(flat, ['email', 'mail']),
    mobile: pickString(flat, ['mobile', 'tel', 'telephone', 'phone']),
    pseudo: pickString(flat, ['pseudo', 'username', 'login']),
    club: pickString(flat, ['club', 'cle_club', 'idclub']),
    favorisClubs: parseFavoritesJson(flat.favoris_clubs ?? flat.favorisClubs),
    favorisMatchs: parseFavoritesJson(flat.favoris_matchs ?? flat.favorisMatchs),
  };
}

function normalizeMatchReactionType(raw: string | undefined): MatchReactionType | null {
  const v = (raw ?? '').trim().toLowerCase();
  return VALID_MATCH_REACTIONS.has(v as MatchReactionType) ? (v as MatchReactionType) : null;
}

function normalizeMatchReaction(raw: RawMatchReaction): {
  cle: string;
  matchId: string;
  tifosi: string;
  reaction: MatchReactionType;
  active: boolean;
} | null {
  const flat = flattenRaw(raw as RawMatch) as RawMatchReaction;
  const reaction = normalizeMatchReactionType(pickString(flat as RawObservation, ['reaction', 'type_reaction', 'type']));
  if (!reaction) return null;
  const matchId =
    pickConnexionId(flat as RawObservation, ['match_football', 'match', 'cle_match']) ??
    pickString(flat as RawObservation, ['match_football', 'match', 'idmatch_football']) ??
    '';
  const tifosi = pickTifosiCle(flat as RawObservation) ?? '';
  const cle = pickString(flat as RawObservation, ['cle', 'id']) ?? '';
  if (!cle || !matchId) return null;
  return {
    cle,
    matchId,
    tifosi,
    reaction,
    active: isFavoriteRowActive(flat as RawObservation),
  };
}

function rowMatchesMatchFootballKey(flat: RawMatchReaction, matchKeyStr: string): boolean {
  const linked = pickConnexionId(flat as RawObservation, [
    'match_football',
    'match',
    'cle_match',
    'idmatch_football',
  ]);
  if (linked === matchKeyStr) return true;
  const candidates = [flat.match_football, flat.match, flat.idmatch_football, flat.matchId, flat.cle_match];
  return candidates.some((v) => v != null && typeof v !== 'object' && String(v).trim() === matchKeyStr);
}

function normalizeFanZoneComment(raw: RawFanZoneComment): FanZoneComment {
  const flat = flattenRaw(raw as RawMatch) as RawFanZoneComment;
  const text = pickString(flat as RawObservation, ['message', 'text', 'commentaire']) ?? '';
  const matchId = pickString(flat as RawObservation, ['match_football', 'matchId', 'idmatch_football']) ?? '';
  const tifosi = pickString(flat as RawObservation, ['tifosi', 'idtifosi', 'tifosi_cle']);
  const author = pickString(flat as RawObservation, ['auteur', 'author', 'pseudo', 'username']) ?? (tifosi || 'Supporteur');
  const createdAt =
    pickString(flat as RawObservation, ['date_creation', 'createdAt', 'date', 'created_at']) ??
    new Date().toISOString();
  const id =
    pickString(flat as RawObservation, ['id', 'cle']) ??
    `fanzone-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return {
    id,
    cle: pickString(flat as RawObservation, ['cle']),
    matchId,
    tifosi,
    author,
    text,
    createdAt,
  };
}

function normalizeCommentTemplate(raw: RawCommentTemplate, index: number): CommentTemplate | null {
  const flat = flattenRaw(raw as RawMatch) as RawCommentTemplate;
  const text = pickString(flat as RawObservation, ['commentaire', 'texte', 'libelle']);
  if (!text) return null;
  const poste = normalizeCommentTemplatePoste(pickString(flat as RawObservation, ['poste']));
  const category = mapCommentTemplateTypeFromApi(pickString(flat as RawObservation, ['type', 'categorie']));
  const id =
    pickString(flat as RawObservation, ['cle', 'id']) ?? `comment-template-${poste}-${index}`;
  const sortOrder = pickNumber(flat as RawObservation, ['ordre', 'order', 'tri', 'sort']);
  const proRaw =
    flat.pro ??
    pickNumber(flat as RawObservation, ['pro']) ??
    pickString(flat as RawObservation, ['pro', 'mode_pro', 'niveau_pro']);
  return {
    id,
    text,
    category,
    poste,
    pro: parseCommentTemplatePro(proRaw),
    frequent: false,
    sortOrder,
  };
}

function normalizeClub(raw: RawClub): Club {
  const logoFields = ['logo', 'logo_url', 'logoUrl', 'image', 'img', 'photo', 'emblem', 'crest'];
  let logo: string | undefined;
  for (const key of logoFields) {
    const val = raw[key];
    if (typeof val === 'string' && val.trim()) {
      logo = val.trim();
      break;
    }
  }
  if (logo && !logo.startsWith('http')) {
    const base = CONFIG.ASSET_BASE_URL.replace(/\/$/, '');
    logo = logo.startsWith('/') ? base + logo : `${base}/${logo}`;
  }
  return {
    idclub: String(raw.idclub ?? raw.id ?? ''),
    cle: raw.cle ? String(raw.cle) : undefined,
    nom: String(raw.nom ?? raw.name ?? ''),
    logo,
    ville: raw.ville ? String(raw.ville) : undefined,
    competition: raw.competition ? String(raw.competition) : undefined,
  };
}

/** Une seule requête réseau à la fois par id (évite doublons React Strict Mode). */
const getMatchByIdInFlight = new Map<string, Promise<ApiResponse<Match> | null>>();

/** Évite deux créations Tifosi concurrentes pour le même email (connexion + sync favoris). */
const upsertTifosiByEmailInFlight = new Map<string, Promise<Tifosi>>();

class ApiService {
  private async fetchWithAuth<T>(endpoint: string, extraHeaders?: Record<string, string>): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.REQUEST_TIMEOUT);
    const fullUrl = CONFIG.API_BASE_URL + endpoint;

    try {
      const response = await fetch(fullUrl, {
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${CONFIG.BEARER_TOKEN}`,
          'Content-Type': 'application/json',
          ...(extraHeaders ?? {}),
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  private extractData<T>(response: unknown): T[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (response && typeof response === 'object') {
      const obj = response as Record<string, unknown>;
      if ('value' in obj && Array.isArray(obj.value)) {
        return obj.value;
      }
      for (const key of Object.keys(obj)) {
        if (Array.isArray(obj[key])) {
          return obj[key] as T[];
        }
      }
    }
    return [];
  }

  private async postWithAuth<TResponse, TBody>(endpoint: string, body: TBody): Promise<TResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.REQUEST_TIMEOUT);
    try {
      const response = await fetch(CONFIG.API_BASE_URL + endpoint, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${CONFIG.BEARER_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return (await response.json()) as TResponse;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  async getMatches(forceRefresh = false): Promise<ApiResponse<Match[]>> {
    const cacheKey = 'matches';

    if (!forceRefresh) {
      const cached = await storageService.get<RawMatch[] | Match[]>(cacheKey);
      if (cached?.length) {
        const normalized = cached.map((item: RawMatch | Match) => normalizeMatch(item as RawMatch));
        return { data: normalized, fromCache: true };
      }
    }

    try {
      const response = await this.fetchWithAuth<unknown>(CONFIG.ENDPOINTS.MATCHES);
      const raw = this.extractData<RawMatch>(response);
      const data = raw.map(normalizeMatch);
      await storageService.set(cacheKey, data);
      return { data, fromCache: false };
    } catch (error) {
      console.error('API matches error:', error);
      const cached = await storageService.get<RawMatch[] | Match[]>(cacheKey);
      if (cached?.length) {
        const normalized = cached.map((item: RawMatch | Match) => normalizeMatch(item as RawMatch));
        return { data: normalized, fromCache: true };
      }
      const meta = await storageService.getWithMeta<RawMatch[] | Match[]>(cacheKey);
      if (meta?.data?.length) {
        const normalized = meta.data.map((item: RawMatch | Match) => normalizeMatch(item as RawMatch));
        return { data: normalized, fromCache: true, expired: true };
      }
      throw error;
    }
  }

  async getClubs(forceRefresh = false): Promise<ApiResponse<Club[]>> {
    const cacheKey = 'clubs';

    if (!forceRefresh) {
      const cached = await storageService.get<Club[]>(cacheKey);
      if (cached) {
        return { data: cached, fromCache: true };
      }
    }

    try {
      const response = await this.fetchWithAuth<unknown>(CONFIG.ENDPOINTS.CLUBS);
      const raw = this.extractData<RawClub>(response);
      const data = raw.map(normalizeClub);
      await storageService.set(cacheKey, data);
      return { data, fromCache: false };
    } catch (error) {
      console.error('API clubs error:', error);
      const cached = await storageService.get<Club[]>(cacheKey);
      if (cached) {
        return { data: cached, fromCache: true };
      }
      const meta = await storageService.getWithMeta<Club[]>(cacheKey);
      if (meta?.data) {
        return { data: meta.data, fromCache: true, expired: true };
      }
      throw error;
    }
  }

  async getMatchById(id: string): Promise<ApiResponse<Match> | null> {
    const key = String(id ?? '').trim();
    if (!key) return null;
    const existing = getMatchByIdInFlight.get(key);
    if (existing) return existing;
    const promise = this.executeGetMatchById(key).finally(() => {
      getMatchByIdInFlight.delete(key);
    });
    getMatchByIdInFlight.set(key, promise);
    return promise;
  }

  private async executeGetMatchById(id: string): Promise<ApiResponse<Match> | null> {
    const cacheKey = `match_${id}`;
    let detailMatch: Match | null = null;
    let fromCache = false;

    try {
      const response = await this.fetchWithAuth<unknown>(
        `${CONFIG.ENDPOINTS.MATCH_DETAIL}/${encodeURIComponent(id)}`,
      );
      detailMatch = normalizeMatch(response as RawMatch);
      await storageService.set(cacheKey, response);
    } catch (error) {
      console.error('API match detail error:', error);
      const cached = await storageService.get<unknown>(cacheKey);
      if (cached) {
        detailMatch = normalizeMatch(cached as RawMatch);
        fromCache = true;
      }
    }

    return detailMatch ? { data: detailMatch, fromCache } : null;
  }

  async getClubById(id: string): Promise<ApiResponse<Club> | null> {
    const result = await this.getClubs();
    const club = result.data.find((c) => c.idclub === id);
    return club ? { data: club, fromCache: result.fromCache } : null;
  }

  async createObservation(payload: ObservationPayload): Promise<unknown> {
    return this.postWithAuth<unknown, ObservationPayload[]>('api/items/observation', [payload]);
  }

  async getObservations(forceRefresh = false): Promise<ApiResponse<Observation[]>> {
    const cacheKey = 'observations';
    if (!forceRefresh) {
      const cached = await storageService.get<RawObservation[]>(cacheKey);
      if (cached?.length) {
        return { data: cached.map(normalizeObservation), fromCache: true };
      }
    }
    try {
      const response = await this.fetchWithAuth<unknown>(CONFIG.ENDPOINTS.OBSERVATIONS);
      const raw = this.extractData<RawObservation>(response);
      await storageService.set(cacheKey, raw);
      return { data: raw.map(normalizeObservation), fromCache: false };
    } catch (error) {
      console.error('API observations error:', error);
      const meta = await storageService.getWithMeta<RawObservation[]>(cacheKey);
      if (meta?.data?.length) {
        return { data: meta.data.map(normalizeObservation), fromCache: true, expired: true };
      }
      return { data: [], fromCache: false };
    }
  }

  /** Liste Tifosi filtrée — en-tête + query pour compatibilité proxy PHP et dev Vite. */
  private async fetchTifosiList(advancedSearch: string): Promise<RawTifosi[]> {
    const listId = CONFIG.TIFOSI_LIST_ID;
    if (!listId) return [];
    const qs = `advancedSearch=${encodeURIComponent(advancedSearch)}`;
    const response = await this.fetchWithAuth<unknown>(`api/list/${listId}?${qs}`, { advancedSearch });
    return this.extractData<RawTifosi>(response);
  }

  private pickCanonicalTifosiRaw(
    rawAll: RawTifosi[],
    matches: (flat: RawTifosi) => boolean,
  ): Tifosi | null {
    const matched = rawAll.filter((row) => {
      const flat = flattenRaw(row as RawMatch) as RawTifosi;
      return matches(flat);
    });
    if (!matched.length) return null;
    matched.sort((a, b) => {
      const flatA = flattenRaw(a as RawMatch) as RawTifosi;
      const flatB = flattenRaw(b as RawMatch) as RawTifosi;
      const idA = Number(flatA.idtifosi ?? flatA.id ?? 0);
      const idB = Number(flatB.idtifosi ?? flatB.id ?? 0);
      return idA - idB;
    });
    return normalizeTifosi(matched[0]);
  }

  async getTifosiByEmail(email: string): Promise<Tifosi | null> {
    const target = email.trim().toLowerCase();
    if (!target) return null;
    const advancedSearch = `email|equal|${target}|`;
    try {
      const rawAll = await this.fetchTifosiList(advancedSearch);
      return this.pickCanonicalTifosiRaw(rawAll, (flat) =>
        (pickString(flat as RawObservation, ['email', 'mail']) || '').toLowerCase().trim() === target,
      );
    } catch (error) {
      console.error('API getTifosiByEmail error:', error);
      return null;
    }
  }

  async getTifosiByCle(cle: string): Promise<Tifosi | null> {
    const target = String(cle || '').trim();
    if (!target) return null;
    const advancedSearch = `cle|equal|${target}|`;
    try {
      const rawAll = await this.fetchTifosiList(advancedSearch);
      return this.pickCanonicalTifosiRaw(rawAll, (flat) =>
        (pickString(flat as RawObservation, ['cle', 'idtifosi', 'id']) || '').trim() === target,
      );
    } catch (error) {
      console.error('API getTifosiByCle error:', error);
      return null;
    }
  }

  async getFavorites(tifosiCle: string): Promise<FavoritesData> {
    const cle = String(tifosiCle || '').trim();
    if (!cle) return { matches: [], clubs: [] };

    if (this.favoritesListConfigured()) {
      return this.getFavoritesFromTables(cle);
    }

    const tifosi = await this.getTifosiByCle(cle);
    if (!tifosi) return { matches: [], clubs: [] };
    return {
      matches: tifosi.favorisMatchs ?? [],
      clubs: tifosi.favorisClubs ?? [],
    };
  }

  private favoritesListConfigured(): boolean {
    const clubId = CONFIG.TIFOSI_CLUB_LIST_ID;
    const matchId = CONFIG.TIFOSI_MATCH_LIST_ID;
    return Boolean(clubId && clubId !== '0' && matchId && matchId !== '0');
  }

  /** Favoris stockés en tables `tifosi_club` / `tifosi_match` (listes GoPaaS configurées). */
  isFavoritesRelational(): boolean {
    return this.favoritesListConfigured();
  }

  private async fetchFavoriteRows<T extends RawObservation>(
    listId: string,
    tifosiCle: string,
  ): Promise<T[]> {
    const advancedSearch = `tifosi|equal|${tifosiCle}|`;
    const response = await this.fetchWithAuth<unknown>(`api/list/${listId}`, { advancedSearch });
    const rawAll = this.extractData<T>(response);
    return rawAll.filter((row) => {
      const flat = flattenRaw(row as RawMatch) as RawObservation;
      if (!isFavoriteRowActive(flat)) return false;
      const rowTifosi = pickConnexionId(flat, ['tifosi', 'idtifosi', 'tifosi_cle']);
      return !rowTifosi || rowTifosi === tifosiCle;
    });
  }

  async getFavoritesFromTables(tifosiCle: string): Promise<FavoritesData> {
    const cle = String(tifosiCle || '').trim();
    if (!cle || !this.favoritesListConfigured()) {
      return { matches: [], clubs: [] };
    }

    try {
      const [clubRows, matchRows] = await Promise.all([
        this.fetchFavoriteRows<RawObservation>(CONFIG.TIFOSI_CLUB_LIST_ID, cle),
        this.fetchFavoriteRows<RawObservation>(CONFIG.TIFOSI_MATCH_LIST_ID, cle),
      ]);

      const clubs = clubRows
        .map((row) => pickConnexionId(flattenRaw(row as RawMatch) as RawObservation, ['club', 'cle_club']))
        .filter((id): id is string => Boolean(id));
      const matches = matchRows
        .map((row) =>
          pickConnexionId(flattenRaw(row as RawMatch) as RawObservation, [
            'match_football',
            'cle_match',
            'match',
          ]),
        )
        .filter((id): id is string => Boolean(id));

      return {
        clubs: [...new Set(clubs)],
        matches: [...new Set(matches)],
      };
    } catch (error) {
      console.error('API getFavoritesFromTables error:', error);
      return { matches: [], clubs: [] };
    }
  }

  async addFavoriteClub(tifosiCle: string, clubCle: string): Promise<void> {
    const tifosi = String(tifosiCle || '').trim();
    const club = String(clubCle || '').trim();
    if (!tifosi || !club) return;
    const payload: TifosiFavoriteClubPayload = {
      cle: favoriteClubCle(tifosi, club),
      tifosi,
      club,
      actif: '1',
    };
    await this.postWithAuth<unknown, TifosiFavoriteClubPayload[]>(CONFIG.ENDPOINTS.TIFOSI_CLUB_ITEMS, [payload]);
  }

  async removeFavoriteClub(tifosiCle: string, clubCle: string): Promise<void> {
    const tifosi = String(tifosiCle || '').trim();
    const club = String(clubCle || '').trim();
    if (!tifosi || !club) return;
    const payload: TifosiFavoriteClubPayload = {
      cle: favoriteClubCle(tifosi, club),
      tifosi,
      club,
      actif: '0',
    };
    await this.postWithAuth<unknown, TifosiFavoriteClubPayload[]>(CONFIG.ENDPOINTS.TIFOSI_CLUB_ITEMS, [payload]);
  }

  async addFavoriteMatch(tifosiCle: string, matchCle: string): Promise<void> {
    const tifosi = String(tifosiCle || '').trim();
    const match = String(matchCle || '').trim();
    if (!tifosi || !match) return;
    const payload: TifosiFavoriteMatchPayload = {
      cle: favoriteMatchCle(tifosi, match),
      tifosi,
      match_football: match,
      actif: '1',
    };
    await this.postWithAuth<unknown, TifosiFavoriteMatchPayload[]>(CONFIG.ENDPOINTS.TIFOSI_MATCH_ITEMS, [payload]);
  }

  async removeFavoriteMatch(tifosiCle: string, matchCle: string): Promise<void> {
    const tifosi = String(tifosiCle || '').trim();
    const match = String(matchCle || '').trim();
    if (!tifosi || !match) return;
    const payload: TifosiFavoriteMatchPayload = {
      cle: favoriteMatchCle(tifosi, match),
      tifosi,
      match_football: match,
      actif: '0',
    };
    await this.postWithAuth<unknown, TifosiFavoriteMatchPayload[]>(CONFIG.ENDPOINTS.TIFOSI_MATCH_ITEMS, [payload]);
  }

  /** Sync complète vers tables relationnelles (+ repli JSON legacy si listes non configurées). */
  async saveFavorites(tifosiCle: string, favorites: FavoritesData): Promise<void> {
    const cle = String(tifosiCle || '').trim();
    if (!cle) return;

    if (this.favoritesListConfigured()) {
      const remote = await this.getFavoritesFromTables(cle);
      const targetClubs = new Set(favorites.clubs);
      const targetMatches = new Set(favorites.matches);

      await Promise.all([
        ...favorites.clubs
          .filter((clubId) => !remote.clubs.includes(clubId))
          .map((clubId) => this.addFavoriteClub(cle, clubId)),
        ...favorites.matches
          .filter((matchId) => !remote.matches.includes(matchId))
          .map((matchId) => this.addFavoriteMatch(cle, matchId)),
        ...remote.clubs
          .filter((clubId) => !targetClubs.has(clubId))
          .map((clubId) => this.removeFavoriteClub(cle, clubId)),
        ...remote.matches
          .filter((matchId) => !targetMatches.has(matchId))
          .map((matchId) => this.removeFavoriteMatch(cle, matchId)),
      ]);
      return;
    }

    const existing = await this.getTifosiByCle(cle);
    if (!existing?.cle) {
      console.warn('saveFavorites: fiche Tifosi introuvable, enregistrement ignoré.');
      return;
    }
    const payload: TifosiPayload = {
      cle,
      favoris_clubs: serializeFavoritesJson(favorites.clubs),
      favoris_matchs: serializeFavoritesJson(favorites.matches),
    };
    await this.postWithAuth<unknown, TifosiPayload[]>(CONFIG.ENDPOINTS.TIFOSI_ITEMS, [payload]);
  }

  async createTifosi(payload: TifosiPayload): Promise<TifosiPayload> {
    await this.postWithAuth<unknown, TifosiPayload[]>(CONFIG.ENDPOINTS.TIFOSI_ITEMS, [payload]);
    return payload;
  }

  async saveTifosi(payload: TifosiPayload): Promise<TifosiPayload> {
    await this.postWithAuth<unknown, TifosiPayload[]>(CONFIG.ENDPOINTS.TIFOSI_ITEMS, [payload]);
    return payload;
  }

  async upsertTifosiFromGoogle(profile: {
    email: string;
    nom?: string;
    prenom?: string;
    pseudo?: string;
    tifosiCle?: string;
  }): Promise<Tifosi> {
    const emailKey = profile.email.trim().toLowerCase();
    if (!emailKey) {
      throw new Error('Email requis pour synchroniser la fiche Tifosi.');
    }

    const inFlight = upsertTifosiByEmailInFlight.get(emailKey);
    if (inFlight) return inFlight;

    const promise = this.executeUpsertTifosiFromGoogle(profile);
    upsertTifosiByEmailInFlight.set(emailKey, promise);
    try {
      return await promise;
    } finally {
      if (upsertTifosiByEmailInFlight.get(emailKey) === promise) {
        upsertTifosiByEmailInFlight.delete(emailKey);
      }
    }
  }

  private async executeUpsertTifosiFromGoogle(profile: {
    email: string;
    nom?: string;
    prenom?: string;
    pseudo?: string;
    tifosiCle?: string;
  }): Promise<Tifosi> {
    const knownCle = String(profile.tifosiCle || '').trim();
    if (knownCle) {
      const byCle = await this.getTifosiByCle(knownCle);
      if (byCle?.cle) return byCle;
    }

    let existing = await this.getTifosiByEmail(profile.email);
    if (!existing?.cle) {
      await new Promise((resolve) => setTimeout(resolve, 120));
      existing = await this.getTifosiByEmail(profile.email);
    }
    if (existing?.cle) return existing;

    const cle =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `tifosi-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const payload: TifosiPayload = {
      cle,
      nom: profile.nom?.trim() || undefined,
      prenom: profile.prenom?.trim() || undefined,
      email: profile.email.trim(),
      pseudo: profile.pseudo?.trim() || undefined,
    };
    await this.createTifosi(payload);
    return {
      cle,
      nom: payload.nom ?? undefined,
      prenom: payload.prenom ?? undefined,
      email: payload.email ?? undefined,
      pseudo: payload.pseudo ?? undefined,
    };
  }

  /** Filtre les lignes brutes dont le champ lien Tifosi correspond à la clé (serveur peut renvoyer la liste complète). */
  private filterObservationsRawByTifosiCle(rows: RawObservation[], tifosiKey: string): RawObservation[] {
    const key = String(tifosiKey || '').trim();
    if (!key) return [];
    return rows.filter((row) => {
      const flat = flattenRaw(row as RawMatch) as RawObservation;
      const cle = pickTifosiCle(flat);
      return matchesObservationOwner({ tifosi: cle }, key);
    });
  }

  async getObservationsByMatch(matchKey: string): Promise<ApiResponse<Observation[]>> {
    if (!matchKey) return { data: [], fromCache: false };
    const matchKeyStr = String(matchKey).trim();
    const cacheKey = `observations_match_${matchKeyStr}`;
    const advancedSearch = `match_football|equal|${matchKeyStr}|`;
    const filterRaw = (rows: RawObservation[]): RawObservation[] => {
      return rows.filter((row) => {
        const flat = flattenRaw(row as RawMatch) as RawObservation;
        const candidates = [
          flat.match_football,
          flat.match,
          flat.idmatch_football,
          flat.matchId,
          flat.cle_match,
        ];
        return candidates.some((v) => v != null && String(v).trim() === matchKeyStr);
      });
    };
    try {
      const response = await this.fetchWithAuth<unknown>(CONFIG.ENDPOINTS.OBSERVATIONS, {
        advancedSearch,
      });
      const rawAll = this.extractData<RawObservation>(response);
      const raw = filterRaw(rawAll);
      await storageService.set(cacheKey, raw);
      const observations = raw.map(normalizeObservation);
      const matchRes = await this.getMatchById(matchKeyStr);
      const enriched = matchRes?.data
        ? observations.map((obs) => enrichObservationWithMatch(obs, matchRes.data))
        : observations;
      return { data: enriched, fromCache: false };
    } catch (error) {
      console.error('API observations match error:', error);
      const meta = await storageService.getWithMeta<RawObservation[]>(cacheKey);
      if (meta?.data?.length) {
        const observations = meta.data.map(normalizeObservation);
        const matchRes = await this.getMatchById(matchKeyStr);
        const enriched = matchRes?.data
          ? observations.map((obs) => enrichObservationWithMatch(obs, matchRes.data))
          : observations;
        return { data: enriched, fromCache: true, expired: true };
      }
      return { data: [], fromCache: false };
    }
  }

  async getObservationsByTifosi(tifosiCle: string, forceRefresh = false): Promise<ApiResponse<Observation[]>> {
    const tifosiKey = String(tifosiCle || '').trim();
    if (!tifosiKey) return { data: [], fromCache: false };
    const cacheKey = `observations_tifosi_${tifosiKey}`;
    const advancedSearch = `tifosi|equal|${tifosiKey}|`;
    if (!forceRefresh) {
      const cached = await storageService.get<RawObservation[]>(cacheKey);
      if (cached?.length) {
        let filtered = this.filterObservationsRawByTifosiCle(cached, tifosiKey);
        if (filtered.length === 0) filtered = cached;
        const data = filtered.map(normalizeObservation);
        return { data, fromCache: true };
      }
    }
    try {
      const response = await this.fetchWithAuth<unknown>(CONFIG.ENDPOINTS.OBSERVATIONS, {
        advancedSearch,
      });
      const rawAll = this.extractData<RawObservation>(response);
      let raw = this.filterObservationsRawByTifosiCle(rawAll, tifosiKey);
      // L’API filtre déjà via advancedSearch ; ne pas vider la liste si le lien tifosi est une relation objet.
      if (raw.length === 0 && rawAll.length > 0) raw = rawAll;
      await storageService.set(cacheKey, raw);
      return { data: raw.map(normalizeObservation), fromCache: false };
    } catch (error) {
      console.error('API observations tifosi error:', error);
      const meta = await storageService.getWithMeta<RawObservation[]>(cacheKey);
      if (meta?.data?.length) {
        let filtered = this.filterObservationsRawByTifosiCle(meta.data, tifosiKey);
        if (filtered.length === 0) filtered = meta.data;
        return { data: filtered.map(normalizeObservation), fromCache: true, expired: true };
      }
      return { data: [], fromCache: false };
    }
  }

  async getFanZoneCommentsByMatch(matchId: string): Promise<ApiResponse<FanZoneComment[]>> {
    const matchKey = String(matchId || '').trim();
    if (!matchKey) return { data: [], fromCache: false };
    const advancedSearch = `match_football|equal|${matchKey}|`;
    try {
      const response = await this.fetchWithAuth<unknown>(CONFIG.ENDPOINTS.FAN_ZONE, {
        advancedSearch,
      });
      const rawAll = this.extractData<RawFanZoneComment>(response);
      const data = rawAll
        .map(normalizeFanZoneComment)
        .filter((comment) => comment.matchId === matchKey)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return { data, fromCache: false };
    } catch (error) {
      console.error('API fan zone match error:', error);
      return { data: [], fromCache: false };
    }
  }

  async createFanZoneComment(payload: FanZoneCommentPayload): Promise<unknown> {
    return this.postWithAuth<unknown, FanZoneCommentPayload[]>(CONFIG.ENDPOINTS.FAN_ZONE_ITEMS, [payload]);
  }

  isMatchReactionsConfigured(): boolean {
    return Boolean(CONFIG.MATCH_REACTION_LIST_ID);
  }

  async getMatchReactionsByMatch(
    matchKey: string,
    tifosiCle?: string,
  ): Promise<ApiResponse<MatchReactionsSummary>> {
    const empty: MatchReactionsSummary = { counts: emptyMatchReactionCounts(), mine: [] };
    const matchKeyStr = String(matchKey || '').trim();
    const listId = CONFIG.MATCH_REACTION_LIST_ID;
    if (!matchKeyStr || !listId) return { data: empty, fromCache: false };

    const advancedSearch = `match_football|equal|${matchKeyStr}|`;
    const cacheKey = `match_reactions_${matchKeyStr}`;
    try {
      const response = await this.fetchWithAuth<unknown>(`api/list/${listId}`, { advancedSearch });
      const rawAll = this.extractData<RawMatchReaction>(response);
      const raw = rawAll.filter((row) => rowMatchesMatchFootballKey(flattenRaw(row as RawMatch) as RawMatchReaction, matchKeyStr));
      await storageService.set(cacheKey, raw);
      return { data: this.aggregateMatchReactions(raw, tifosiCle), fromCache: false };
    } catch (error) {
      console.error('API match reactions error:', error);
      const meta = await storageService.getWithMeta<RawMatchReaction[]>(cacheKey);
      if (meta?.data?.length) {
        return { data: this.aggregateMatchReactions(meta.data, tifosiCle), fromCache: true, expired: true };
      }
      return { data: empty, fromCache: false };
    }
  }

  async setMatchReaction(params: {
    tifosiCle: string;
    matchKey: string;
    reaction: MatchReactionType;
    active: boolean;
  }): Promise<unknown> {
    const tifosi = String(params.tifosiCle || '').trim();
    const match = String(params.matchKey || '').trim();
    if (!tifosi || !match) throw new Error('tifosi ou match manquant');
    const payload: MatchReactionPayload = {
      cle: matchReactionCle(tifosi, match, params.reaction),
      tifosi,
      match_football: match,
      reaction: params.reaction,
      actif: params.active ? '1' : '0',
    };
    return this.postWithAuth<unknown, MatchReactionPayload[]>(CONFIG.ENDPOINTS.MATCH_REACTION_ITEMS, [payload]);
  }

  private aggregateMatchReactions(raw: RawMatchReaction[], tifosiCle?: string): MatchReactionsSummary {
    const counts = emptyMatchReactionCounts();
    const mine: MatchReactionType[] = [];
    const me = String(tifosiCle || '').trim();
    for (const row of raw) {
      const normalized = normalizeMatchReaction(row);
      if (!normalized?.active) continue;
      counts[normalized.reaction] += 1;
      if (me && normalized.tifosi && matchesObservationOwner({ tifosi: normalized.tifosi }, me)) {
        mine.push(normalized.reaction);
      }
    }
    return { counts, mine };
  }

  async getCommentTemplates(forceRefresh = false): Promise<ApiResponse<CommentTemplate[]>> {
    const cacheKey = 'comment_templates';
    if (!forceRefresh) {
      const cached = await storageService.get<CommentTemplate[]>(cacheKey);
      if (cached?.length) {
        return { data: cached, fromCache: true };
      }
    }
    try {
      const response = await this.fetchWithAuth<unknown>(CONFIG.ENDPOINTS.COMMENT_TEMPLATES);
      const raw = this.extractData<RawCommentTemplate>(response);
      const data = raw
        .map((row, index) => normalizeCommentTemplate(row, index))
        .filter((row): row is CommentTemplate => row != null);
      await storageService.set(cacheKey, data);
      return { data, fromCache: false };
    } catch (error) {
      console.error('API comment templates error:', error);
      const meta = await storageService.getWithMeta<CommentTemplate[]>(cacheKey);
      if (meta?.data?.length) {
        return { data: meta.data, fromCache: true, expired: true };
      }
      return { data: [], fromCache: false };
    }
  }

  /**
   * Crée une fiche `tifosi_event` (pas de `date_creation` / `heure_creation` : gérés par GoPaaS).
   * `club_opened` : renseigner `club` (legacy : `match_football` peut porter l’id club).
   */
  async createUsageEvent(payload: UsageEventInput): Promise<unknown> {
    const cle =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `tifosi-event-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const body: UsageEventPayload = {
      cle,
      type: payload.type,
      tifosi: payload.tifosi,
    };
    const matchKey = String(payload.match_football ?? '').trim();
    const clubKey = String(payload.club ?? '').trim();
    const obsKey = String(payload.observation ?? '').trim();
    const source = String(payload.source ?? '').trim();
    if (matchKey) body.match_football = matchKey;
    if (clubKey) body.club = clubKey;
    if (obsKey) body.observation = obsKey;
    if (source) body.source = source.slice(0, 240);
    return this.postWithAuth<unknown, UsageEventPayload[]>(CONFIG.ENDPOINTS.TIFOSI_EVENT_ITEMS, [body]);
  }
}

export const apiService = new ApiService();
export default apiService;
